'use client'

import { type AnchorHTMLAttributes, type ReactNode, useEffect, useRef } from 'react'
import { useAuth } from '@/contexts/auth-context'
import {
	createBlogReportCtaCapture,
	type ReportCtaIdentity,
	type ReportCtaPlacement,
} from '@/lib/report-cta-analytics'

function useReportCtaCapture() {
	const { user, loading, isAuthenticated } = useAuth()
	const identity = useRef<ReportCtaIdentity>({ loading, user: null })
	identity.current = {
		loading,
		user: isAuthenticated && user ? { userId: user.userId, role: user.role } : null,
	}
	const capture = useRef<ReturnType<typeof createBlogReportCtaCapture> | null>(null)
	if (!capture.current) capture.current = createBlogReportCtaCapture(() => identity.current)
	return capture.current
}

/** Delegates only public report links in server-rendered article HTML. */
export function ArticleReportCtaTracking({ postSlug, children }: { postSlug: string; children?: ReactNode }) {
	const container = useRef<HTMLDivElement>(null)
	const capture = useReportCtaCapture()

	useEffect(() => {
		const root = container.current
		if (!root) return
		function onClick(event: MouseEvent) {
			if (event.defaultPrevented || (event.type === 'click' ? event.button !== 0 : event.button !== 1)) return
			const anchor = event.target instanceof Element ? event.target.closest('a') : null
			if (!anchor || !root?.contains(anchor) || anchor.hasAttribute('data-blog-report-cta-tracked')) return
			capture({ href: anchor.href, postSlug, placement: 'article-inline' })
		}
		root.addEventListener('click', onClick)
		root.addEventListener('auxclick', onClick)
		return () => {
			root.removeEventListener('click', onClick)
			root.removeEventListener('auxclick', onClick)
		}
	}, [postSlug, capture])

	return <div ref={container}>{children}</div>
}

/** A normal anchor: tracking never prevents, replaces or waits for navigation. */
export function TrackedReportCtaLink({
	href,
	postSlug,
	placement,
	onClick,
	onAuxClick,
	...props
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
	href: string
	postSlug: string
	placement: ReportCtaPlacement
}) {
	const capture = useReportCtaCapture()
	return (
		<a
			{...props}
			href={href}
			data-blog-report-cta-tracked=""
			onClick={(event) => {
				onClick?.(event)
				if (!event.defaultPrevented && event.button === 0) {
					capture({ href: event.currentTarget.href, postSlug, placement })
				}
			}}
			onAuxClick={(event) => {
				onAuxClick?.(event)
				if (!event.defaultPrevented && event.button === 1) {
					capture({ href: event.currentTarget.href, postSlug, placement })
				}
			}}
		/>
	)
}
